'use client';

/**
 * Root-layout error boundary — catches render-phase errors that escape the
 * dashboard's `(dashboard)/error.tsx` boundary (e.g. crashes in
 * `src/app/layout.tsx` itself). Next.js App Router requires this file to
 * render its own <html>/<body> since it replaces the root layout.
 *
 * Renders the canonical user-facing copy from `@auctogon/core/api` with no
 * reference ID — pure-client errors don't carry a server requestId to
 * surface.
 *
 * ## Every size here is inline, and must stay that way
 *
 * This file REPLACES the root layout, so it renders its own `<html>`/`<body>`
 * and the root layout's stylesheet imports — Tailwind and
 * `generated-typography.css`, which is where `--type-*` and every `text-<step>`
 * class come from — are not in the document. A class would name a step that
 * resolves to nothing, on the one screen whose entire job is to stay legible
 * when the app has already failed. Same reason the colours here are hex
 * literals rather than brand tokens.
 *
 * So the `type/no-raw-text-size` disables below are structural, not deferred
 * work: they do not drain, and the rem values are chosen to land on the scale's
 * steps (1.5rem = 24 = title-lg, 1.25rem = 20 = title, 0.875rem = 14 = body) so
 * this page still LOOKS like the rest of the admin even though it cannot share
 * its stylesheet.
 *
 * See `.claude/rules/error-handling-chain.md` for the bubble + middleware pattern.
 */
import { useEffect } from 'react';
import { formatErrorMessage } from '@auctogon/core/api';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Client-side only: the boot-time console patch is server-only (bails
    // when `window` is defined). Browser RUM was removed (BL-1267 / ADR 043),
    // so this render-phase error reaches the browser console only — no
    // telemetry backend. Server-side crashes still surface via error.digest +
    // the Cloud Trace span from apiHandler.
    console.error('[Global Error Boundary]', error);
  }, [error]);

  return (
    <html>
      <body>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: '1rem',
            padding: '2rem',
            fontFamily:
              'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            color: '#111827',
          }}
        >
          <div
            style={{
              width: '3.5rem',
              height: '3.5rem',
              borderRadius: '9999px',
              background: '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // eslint-disable-next-line type/no-raw-text-size -- 24px (title-lg). No stylesheet here: this file replaces the root layout, so no class resolves. See the header.
              fontSize: '1.5rem',
            }}
            aria-hidden="true"
          >
            ⚠
          </div>
          {/* eslint-disable-next-line type/no-raw-text-size -- 20px (title). No stylesheet here — see the header. */}
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>
            Something went wrong
          </h2>
          <p
            style={{
              // eslint-disable-next-line type/no-raw-text-size -- 14px (body). No stylesheet here — see the header.
              fontSize: '0.875rem',
              color: '#4b5563',
              textAlign: 'center',
              maxWidth: '28rem',
              margin: 0,
            }}
          >
            {formatErrorMessage()}
          </p>
          {/* eslint-disable-next-line designsystem/no-raw-button -- this screen replaces the root layout, so no stylesheet (and no Button styling) loads; see the file header */}
          <button
            type="button"
            onClick={reset}
            style={{
              // eslint-disable-next-line brandhex/no-hardcoded-brand-hex -- no stylesheet loads on this screen (file header), so the `--primary` var is undefined here; the literal is the only colour that renders
              background: '#09514d',
              color: '#fff',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: 'none',
              cursor: 'pointer',
              // eslint-disable-next-line type/no-raw-text-size -- 14px (body). No stylesheet here — see the header.
              fontSize: '0.875rem',
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}
